import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getStudioApprovals,
  type AdminStudioApprovalSummary,
  type StudioApprovalStatus,
} from '../../api/admin';
import { ApiError } from '../../api/client';

const STATUS_TABS: Array<{ value: StudioApprovalStatus; label: string }> = [
  { value: 'PENDING', label: '심사 대기' },
  { value: 'APPROVED', label: '승인 완료' },
  { value: 'REJECTED', label: '반려' },
  { value: 'NONE', label: '미신청' },
];

const APPROVAL_STATUS_LABELS: Record<string, string> = {
  NONE: '미신청',
  PENDING: '심사 대기',
  APPROVED: '승인 완료',
  REJECTED: '반려',
};

function approvalBadgeClass(status: string) {
  if (status === 'APPROVED') return 'partner-badge partner-badge--green';
  if (status === 'PENDING') return 'partner-badge partner-badge--yellow';
  if (status === 'REJECTED') return 'partner-badge partner-badge--red';
  return 'partner-badge';
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');
  return `${parsed.getFullYear()}.${month}.${day} ${hour}:${minute}`;
}

export function AdminStudioApprovalsPage() {
  const [status, setStatus] = useState<StudioApprovalStatus>('PENDING');
  const [items, setItems] = useState<AdminStudioApprovalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getStudioApprovals(status)
      .then((list) => {
        if (cancelled) return;
        setItems(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(
          err instanceof ApiError && err.message ? err.message : '심사 목록을 불러오지 못했습니다.',
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <>
      <h1 className="partner-title">업체 승인 심사</h1>
      <p className="partner-subtitle">
        점주가 제출한 업체 등록 신청을 심사합니다. 제출이 오래된 순으로 표시됩니다.
      </p>

      <div className="admin-tabs" role="tablist" aria-label="승인 상태 필터">
        {STATUS_TABS.map((tab) => (
          <button
            aria-selected={status === tab.value}
            className={`admin-tab${status === tab.value ? ' admin-tab--active' : ''}`}
            key={tab.value}
            onClick={() => setStatus(tab.value)}
            role="tab"
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error ? <div className="partner-error">{error}</div> : null}

      {loading ? (
        <p className="partner-empty">불러오는 중…</p>
      ) : items.length === 0 ? (
        <div className="partner-card">
          <p className="partner-empty">
            {status === 'PENDING'
              ? '심사 대기 중인 업체가 없습니다.'
              : `'${APPROVAL_STATUS_LABELS[status]}' 상태의 업체가 없습니다.`}
          </p>
        </div>
      ) : (
        <div className="partner-card">
          <div className="partner-table-wrap">
            <table className="partner-table">
              <thead>
                <tr>
                  <th>업체</th>
                  <th>주소</th>
                  <th>점주 ID</th>
                  <th>제출일</th>
                  <th>상태</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.studioId}>
                    <td>
                      <div className="admin-approval-cell">
                        {item.primaryImageUrl ? (
                          <img
                            alt=""
                            className="admin-approval-thumb"
                            src={item.primaryImageUrl}
                          />
                        ) : (
                          <span
                            aria-hidden
                            className="admin-approval-thumb admin-approval-thumb--empty"
                          >
                            없음
                          </span>
                        )}
                        <span className="admin-cell-main">{item.name}</span>
                      </div>
                    </td>
                    <td>{item.roadAddress ?? '—'}</td>
                    <td>
                      <span className="admin-cell-sub">{item.ownerUserId}</span>
                    </td>
                    <td>{formatDateTime(item.submittedAt)}</td>
                    <td>
                      <span className={approvalBadgeClass(String(item.approvalStatus))}>
                        {APPROVAL_STATUS_LABELS[String(item.approvalStatus)] ??
                          item.approvalStatus}
                      </span>
                    </td>
                    <td>
                      <Link
                        className="partner-btn partner-btn--ghost partner-btn--sm"
                        to={`/admin/studio-approvals/${item.studioId}`}
                      >
                        상세
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
